import { WorkInstructionControlRoomDto } from '@operations/app/api'
import { HousekeepingGroupViewModel, OTHERS_GROUP_ID } from '../models/work-instruction-view.model'

// Structural shape of yard's HousekeepingPlanSummaryResponse, declared locally so this
// helper (and its unit tests) don't transitively pull in @tom-ui/yard — which drags
// maplibre into the vitest bundle and crashes jsdom on import.
export interface HousekeepingPlanSummaryLike {
  id: string
  name?: string | null
  completedMovesCount: number
  totalMovesCount: number
}

const PROGRESS_LABEL_FALLBACK = '0/0'

export const formatProgressLabel = (completed: number, total: number) =>
  total === 0 ? PROGRESS_LABEL_FALLBACK : `${completed}/${total}`

export const fallbackPlanName = (id: string) => `Plan ${id.slice(0, 8)}`

export const uniqueHousekeepingPlanIds = (
  workInstructions: WorkInstructionControlRoomDto[],
): string[] => {
  const seen = new Set<string>()
  for (const wi of workInstructions) {
    if (wi.housekeepingPlanId) seen.add(wi.housekeepingPlanId)
  }
  return [...seen]
}

export const buildHousekeepingGroups = (
  summaries: HousekeepingPlanSummaryLike[],
  othersLabel: string,
  includeOthers: boolean,
): HousekeepingGroupViewModel[] => {
  const groups: HousekeepingGroupViewModel[] = summaries.map(s => ({
    id: s.id,
    name: s.name ?? fallbackPlanName(s.id),
    progressLabel: formatProgressLabel(s.completedMovesCount, s.totalMovesCount),
    completedMovesCount: s.completedMovesCount,
    totalMovesCount: s.totalMovesCount,
    isOthers: false,
  }))

  // Named plans alphabetically, then unnamed plans by id at the end.
  groups.sort((a, b) => {
    const aHasName = !a.name.startsWith('Plan ')
    const bHasName = !b.name.startsWith('Plan ')
    if (aHasName !== bHasName) return aHasName ? -1 : 1
    return a.name.localeCompare(b.name)
  })

  if (includeOthers) {
    groups.push({
      id: OTHERS_GROUP_ID,
      name: othersLabel,
      progressLabel: '',
      completedMovesCount: 0,
      totalMovesCount: 0,
      isOthers: true,
    })
  }

  return groups
}

export const hasUngroupedInternalWorkInstructions = (
  workInstructions: { housekeepingPlanId?: string | null }[],
): boolean => workInstructions.some(wi => !wi.housekeepingPlanId)
